/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  makeStyles,
  FormControl,
  Select,
  MenuItem,
  Button
} from "@material-ui/core";
import { PrintOutlined } from "@material-ui/icons";
import Swal from "sweetalert2";
import fileDownload from "js-file-download";
import { getArbitratorDashboard, printArbitratorDashboardReport } from "../../service";
import { displayEmail, isCaseReopened, safeDateFormat } from "../../utils";
import MandateClock from "../MandateClock";
import ReopenedChip from "../ReopenedChip";

// The `language` field on the arbitrator master is free text, but the API may
// also send it back as a list — normalise both shapes into one display string.
const formatLanguages = (language) => {
  if (Array.isArray(language)) {
    const list = language
      .map((item) => (typeof item === "string" ? item.trim() : item?.name || ""))
      .filter(Boolean);
    return list.length ? list.join(", ") : "—";
  }
  if (typeof language === "string" && language.trim()) {
    return language.trim();
  }
  return "—";
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
  },
  breadcrumbs: {
    marginBottom: theme.spacing(3),
    fontSize: "0.875rem",
    "& a": {
      textDecoration: "none",
      color: theme.palette.text.secondary,
      fontWeight: 500,
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
  },
  profileCard: {
    padding: theme.spacing(3),
    borderRadius: 8,
    backgroundColor: "#ffffff",
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    marginBottom: theme.spacing(3),
  },
  profileHeader: {
    marginBottom: theme.spacing(3),
    borderBottom: "1px solid #E5E5E5",
    paddingBottom: theme.spacing(2),
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(2),
    },
  },
  printButton: {
    borderRadius: 4,
    border: "1px solid #AAAFB9",
    backgroundColor: "#ffffff",
    textTransform: "none",
    fontWeight: 500,
    // padding: "0.5rem 1rem",
    color: TEXT.primary,
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  profileName: {
    fontWeight: 600,
    fontSize: "1.5rem",
    color: TEXT.primary,
  },
  profileSub: {
    fontSize: "0.875rem",
    color: TEXT.muted,
    marginTop: 2,
  },
  infoItem: {
    padding: theme.spacing(1, 0),
  },
  infoLabel: {
    fontSize: "0.75rem",
    color: TEXT.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 600,
    marginBottom: 4,
  },
  infoValue: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: TEXT.primary,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "1.2rem",
    marginBottom: theme.spacing(2),
    color: TEXT.primary,
  },
  statCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: "#ffffff",
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    textAlign: "center",
    height: "100%",
  },
  statLabel: {
    fontSize: "0.75rem",
    color: TEXT.muted,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: TEXT.primary,
    marginTop: theme.spacing(1),
  },
  tableCard: {
    padding: theme.spacing(3),
    borderRadius: 8,
    backgroundColor: "#ffffff",
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  tableContainer: {
    borderRadius: 8,
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    marginTop: theme.spacing(2),
    maxWidth: "100%",
  },
  tableCell: {
    padding: "0.62rem",
    fontSize: "0.875rem",
    borderBottom: "1px solid #E5E5E5",
  },
  headerRow: {
    backgroundColor: "#eeeeee",
  },
  headerText: {
    color: TEXT.primary,
    fontWeight: 600,
  },
  statusBadge: {
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: 4,
  },
  placeholderText: {
    color: TEXT.muted,
    fontSize: "0.875rem",
  },
  errorText: {
    fontSize: "0.875rem",
    color: TEXT.error,
  },
}));

export default function ArbitratorDashboard({ arbitratorId, onBack }) {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [caseTypeFilter, setCaseTypeFilter] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [printing, setPrinting] = useState(false);

  const handlePrintReport = async () => {
    if (!arbitratorId) return;
    setPrinting(true);
    const params = {};
    if (caseTypeFilter) {
      params.type = caseTypeFilter;
    }

    try {
      const res = await printArbitratorDashboardReport(arbitratorId, params);
      const name = data?.arbitrator?.name ? data.arbitrator.name.replace(/\s+/g, "_") : arbitratorId;
      fileDownload(res.data, `arbitrator_${name}_report.pdf`);
    } catch (err) {
      console.error("Error downloading arbitrator report:", err);
      let message = "Could not generate report";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          message = json.message || json.error || message;
        } catch (_) { }
      } else if (err.response?.data?.message || err.response?.data?.error) {
        message = err.response.data.message || err.response.data.error;
      }
      Swal.fire({
        icon: "error",
        title: "Print Report Failed",
        text: message,
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setPrinting(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (!arbitratorId) return;

    setLoading(true);
    setError("");

    const params = {};
    if (caseTypeFilter) {
      params.type = caseTypeFilter;
    }

    getArbitratorDashboard(arbitratorId, params)
      .then((res) => {
        if (isMounted) {
          if (res.data) {
            setData(res.data);
          } else {
            setError("Failed to load dashboard data.");
          }
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Error fetching arbitrator dashboard:", err);
          setError(
            err.response?.data?.message || "Failed to fetch arbitrator dashboard."
          );
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [arbitratorId, caseTypeFilter]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" style={{ gap: "1rem" }}>
        <CircularProgress size={44} />
        <Typography className={classes.placeholderText}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            Arbitrators
          </Link>
          <Typography color="textPrimary">Dashboard</Typography>
        </Breadcrumbs>
        <Paper className={classes.profileCard} style={{ textAlign: "center" }}>
          <Typography className={classes.errorText} variant="body1">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const { arbitrator, summary, cases = [] } = data || {};

  const statItems = [
    { label: "Total Cases", value: summary?.total_cases ?? 0 },
    { label: "Open Cases", value: summary?.open_cases ?? 0 },
    { label: "Disposal", value: summary?.closed_cases ?? 0 },
    {
      label: "Avg. Disposal Time",
      value:
        summary?.avg_disposal_days !== null && summary?.avg_disposal_days !== undefined
          ? `${summary.avg_disposal_days} days`
          : "N/A",
    },
    { label: "Total Hearings", value: summary?.total_hearings ?? 0 },
    { label: "Total Adjournments", value: summary?.total_adjournments ?? 0 },
  ];

  const getStatusChip = (status, caseRow) => {
    if (isCaseReopened({ ...caseRow, status })) {
      return <ReopenedChip reopenDate={caseRow?.reopen_date} reopenReason={caseRow?.reopen_reason} />;
    }
    if (!status) return "—";
    const s = status.toLowerCase();
    if (s === "ongoing" || s === "open") {
      return (
        <Chip
          label="Ongoing"
          className={classes.statusBadge}
          style={{ backgroundColor: "#e8f5e9", color: TEXT.success }}
          size="small"
        />
      );
    }
    if (s === "closed" || s === "resolved") {
      return (
        <Chip
          label="Disposal"
          className={classes.statusBadge}
          style={{ backgroundColor: "#eeeeee", color: TEXT.muted }}
          size="small"
        />
      );
    }
    return <Chip label={status} className={classes.statusBadge} size="small" />;
  };

  const getFinalAwardChip = (passed) => {
    if (passed) {
      return (
        <Chip
          label="Yes"
          className={classes.statusBadge}
          style={{ backgroundColor: "#e3f2fd", color: TEXT.info }}
          size="small"
        />
      );
    }
    return (
      <Chip
        label="No"
        className={classes.statusBadge}
        style={{ backgroundColor: "#f5f5f5", color: TEXT.muted }}
        size="small"
      />
    );
  };

  return (
    <Box className={classes.root}>
      {/* 1. Page Header with Breadcrumbs & Action Button */}
      <Box className={classes.headerContainer}>
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs} style={{ marginBottom: 0 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            Arbitrators
          </Link>
          <Typography color="textPrimary">Dashboard</Typography>
        </Breadcrumbs>
        <Button
          variant="outlined"
          className={classes.printButton}
          onClick={handlePrintReport}
          disabled={printing}
          startIcon={
            printing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PrintOutlined />
            )
          }
        >
          {printing ? "Generating..." : "Print Report"}
        </Button>
      </Box>

      {/* 2. Arbitrator Profile Card */}
      <Paper className={classes.profileCard}>
        <Box className={classes.profileHeader}>
          <Typography className={classes.profileName}>
            {arbitrator?.name || "—"}
          </Typography>
          <Typography className={classes.profileSub}>
            Arbitrator Profile
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Email Address</Typography>
              <Typography className={classes.infoValue}>
                {displayEmail(arbitrator?.email)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Phone Number</Typography>
              <Typography className={classes.infoValue}>
                {arbitrator?.phone || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Date of Birth</Typography>
              <Typography className={classes.infoValue}>
                {arbitrator?.dob || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Age</Typography>
              <Typography className={classes.infoValue}>
                {arbitrator?.age !== null && arbitrator?.age !== undefined
                  ? `${arbitrator.age} years`
                  : "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Category</Typography>
              <Typography className={classes.infoValue}>
                {arbitrator?.category || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Languages</Typography>
              <Typography className={classes.infoValue}>
                {formatLanguages(arbitrator?.language)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 3. Summary Cards */}
      <Box mb={7}>
        <Grid container spacing={3}>
          {statItems.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statLabel}>{item.label}</Typography>
                <Typography className={classes.statValue}>{item.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* 4. Cases Table Header & Filter */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography className={classes.sectionTitle} style={{ margin: 0 }}>
          Case Details
        </Typography>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <Select
            value={caseTypeFilter}
            onChange={(e) => setCaseTypeFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Cases</MenuItem>
            <MenuItem value="sole">Sole Arbitrator</MenuItem>
            <MenuItem value="tribunal">Tribunal</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* 4. Cases Table */}
      <Paper>
        {cases.length === 0 ? (
          <Typography className={classes.placeholderText}>
            No cases assigned to this arbitrator.
          </Typography>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table size="small" style={{ maxWidth: "100%", marginTop: 0 }}>
              <TableHead>
                <TableRow className={classes.headerRow}>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`}>
                    Case Number
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`}>
                    Case Name
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`}>
                    Status
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`}>
                    Current Stage
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`}>
                    Mandate / Time Left
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`} align="center">
                    Hearings
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`} align="center">
                    Next Hearing Date
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`} align="center">
                    Final Award Passed
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`}>
                    Registered On
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`}>
                    Date of Disposal
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.headerText}`} align="center">
                    Disposal Days
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cases.map((c, index) => (
                  <TableRow key={c.case_id || index} hover>
                    <TableCell className={classes.tableCell}>
                      {c.case_id || "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {c.case_name || "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {getStatusChip(c.status, c)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {c.stage || "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <MandateClock mandate={c.mandate} compact />
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      {c.hearing_count ?? 0}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      {safeDateFormat(c.next_hearing_date) ?? "-"}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      {getFinalAwardChip(c.final_award_passed)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {safeDateFormat(c.registered_on)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {c.closed_on ? safeDateFormat(c.closed_on) : "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      {c.disposal_days !== null && c.disposal_days !== undefined
                        ? c.disposal_days
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
